/**
 * PWA Hook
 *
 * Handles PWA registration, update notifications, and offline detection.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { logger } from '../utils/logger';

interface PWAState {
  needRefresh: boolean;
  offlineReady: boolean;
  isOnline: boolean;
}

interface UsePWAReturn extends PWAState {
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
  closePrompt: () => void;
}

// Update check interval: 1 hour
const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

export function usePWA(): UsePWAReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const updateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      if (registration) {
        // Check for updates periodically
        updateIntervalRef.current = setInterval(() => {
          registration.update();
        }, SW_UPDATE_INTERVAL_MS);
      }
    },
    onRegisterError(error) {
      logger.error('SW registration error', error);
    },
  });

  // Cleanup update interval on unmount
  useEffect(() => {
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const closePrompt = useCallback(() => {
    setNeedRefresh(false);
    setOfflineReady(false);
  }, [setNeedRefresh, setOfflineReady]);

  return {
    needRefresh,
    offlineReady,
    isOnline,
    updateServiceWorker,
    closePrompt,
  };
}

export default usePWA;
